import rewind from '@mapbox/geojson-rewind';
import { bbox, bboxPolygon, lineString, transformScale } from '@turf/turf';
import {
  Feature,
  GeoJsonProperties,
  Geometry,
  MultiPolygon,
  Point,
  Polygon,
  Position,
} from 'geojson';

import centroid from '../centroid';
import Logger from '../logger';

/**
 * Get less precise coordinates
 * @param coordinates Position[][] - Polygon coordinates
 * @param precision
 * @returns
 */
function getLowerResolutionPolygonCoordinates(
  coordinates: Position[][],
  precision: number
) {
  return coordinates.map((p1) =>
    p1.map((p2) => p2.map((coord) => parseFloat(coord.toFixed(precision))))
  );
}

/**
 * Get a less precision feature
 * @param geometry
 * @returns
 */
export function getLowerResFeature(
  geometry: Geometry,
  properties?: GeoJsonProperties,
  precision = 6
): Feature {
  switch (geometry.type) {
    case 'Polygon': {
      const newGeometry: Polygon = {
        ...geometry,
        coordinates: getLowerResolutionPolygonCoordinates(
          geometry.coordinates,
          precision
        ),
      };

      const feature: Feature = {
        geometry: newGeometry,
        properties: properties ?? {},
        type: 'Feature',
      };

      return rewind(feature, false);
    }
    case 'MultiPolygon': {
      const newGeometry: MultiPolygon = {
        ...geometry,
        coordinates: geometry.coordinates.map((position) =>
          getLowerResolutionPolygonCoordinates(position, precision)
        ),
      };
      const feature: Feature = {
        geometry: newGeometry,
        properties: properties ?? {},
        type: 'Feature',
      };

      return rewind(feature, false);
    }
    default: {
      const feature: Feature = {
        geometry,
        properties: properties ?? {},
        type: 'Feature',
      };

      return feature;
    }
  }
}
/**
 * This will create an optimized bbox around all of the features, and return the bbox as a GeoJSON.
 * It will create a line string with the centroids, it will draw a bbox, scale it, and then turn it into a GeoJSON.
 */
export function getScaledContainedFeature(
  features: Feature<Polygon | MultiPolygon>[],
  scaleFactor = 1.5
) {
  if (features.length === 0) {
    return null;
  }

  if (features.length === 1) {
    const feat = features[0];
    // If it doesn't have coordinates, then ignore this feature
    if (!feat.geometry.coordinates.length) {
      return null;
    }
    return transformScale(feat, scaleFactor);
  }

  try {
    const centers: Feature<Point>[] = [];

    features.forEach((feature) => {
      const center = centroid(feature);

      if (center) {
        centers.push(center);
      }
    });
    const centerPoints = centers.map((point) => point.geometry.coordinates);
    const line = lineString(centerPoints);
    const theBox = bbox(line);
    const feature = bboxPolygon(theBox);
    const scaledFeature = transformScale(feature, scaleFactor);

    return scaledFeature;
  } catch (err) {
    Logger.error(err);
  }

  return null;
}

type GeometryType =
  | 'Point'
  | 'MultiPoint'
  | 'LineString'
  | 'MultiLineString'
  | 'Polygon'
  | 'MultiPolygon'
  | 'GeometryCollection';

/**
 * Determines if the geometry is a polygon or multi-polygon
 * @param geometryType GeoJSON geometry Type
 * @returns true if the geometry type is a polygon or multi-polygon
 */
export function isPolygonic(geometryType: GeometryType) {
  return ['MultiPolygon', 'Polygon'].includes(geometryType);
}
